/**
 * SortableTable
 *
 * Adds a button into each column header
 * allowing the button to reorder the table
 * contents by descending or ascending order.
 */

const enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

interface TableCellData {
  raw: string | number;
  original: string;
}
export class SortableTable {
  private table!: HTMLElement;
  private data: TableCellData[][] = [];

  constructor(table: HTMLElement | null) {
    if (!table) return;

    this.table = table;

    // Fetch column headings
    const columnHeadings = table.querySelector('thead')
      ? table.querySelectorAll('thead tr:last-child > td')
      : table.querySelectorAll('tr:first-child > td');

    columnHeadings.forEach((e: Element, idx) => {
      const btn = document.createElement('button');
      const elm = e as HTMLElement;

      btn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" version="1.1" x="0" y="0" viewBox="0 0 100 125" width="10">
        <polygon points="49.3,86.2 5.3,42.2 15.2,32.3 49.3,66.4 84.1,31.7 94,41.6 "></polygon>
      </svg>`;
      btn.dataset.order = elm.dataset?.sortOrder || SortOrder.DESC;

      btn.addEventListener('click', (e: MouseEvent) => {
        // Reset all other buttons
        this.resetUnselectedSortButtons(idx);

        if (!e.target) {
          return;
        }

        const btnElement = e.target as HTMLButtonElement;
        btnElement.classList.add('c-brand');

        // 2. Sort items based on dataset-attribute
        const sortOrderAscending = btnElement.dataset.order === SortOrder.ASC;
        const sortOrder = sortOrderAscending ? 1 : -1;

        renderItems(this.sortData(idx, sortOrder));

        btnElement.dataset.order = sortOrderAscending ? SortOrder.DESC : SortOrder.ASC;
      });
      e.appendChild(btn);
    });

    // 1. Move items to data structure (done once)
    this.data = this.getDataFromTable();

    const tbody = document.createElement('tbody');
    tbody.className = 'generated-content';
    table.appendChild(tbody);

    // 3. Rerender data items
    renderItems(this.data);

    function renderItems(dataRows: TableCellData[][]) {
      tbody.innerHTML = '';
      dataRows.forEach((row: TableCellData[]) => {
        const tr = document.createElement('tr');
        tr.innerHTML = row.map(cell => `<td>${cell.original}</td>`).join('');
        tbody.append(tr);
      });
    }
  }

  sortData(sortColumn: number, sortOrder: number) {
    return this.data.sort((a, b) => {
      return a[sortColumn].raw > b[sortColumn].raw ? sortOrder : sortOrder * -1;
    });
  }

  getDataFromTable(): TableCellData[][] {
    const data: TableCellData[][] = [];

    const tableCellSelector = this.table.querySelector('thead')
      ? 'tbody tr'
      : 'tr:not(:first-child)';

    this.table.querySelectorAll(tableCellSelector).forEach(el => {
      const row: TableCellData[] = [];
      el.querySelectorAll('td').forEach(cell => {
        const dataSource = cell.dataset.raw || cell.innerHTML;
        const raw = parseFloat(dataSource.replace(/[^0-9\\.]+/g, ''));
        row.push({
          raw: isNaN(raw) ? cell.innerHTML : raw,
          original: cell.innerHTML,
        });

        cell.remove();
      });
      el.remove();
      data.push(row);
    });

    return data;
  }

  resetUnselectedSortButtons(pointer: number) {
    this.table.querySelectorAll('button').forEach((el, buttonIdx) => {
      if (pointer !== buttonIdx) {
        el.dataset.order = SortOrder.DESC;
        el.classList.remove('c-brand');
      }
    });
  }
}
